import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useAuth } from '../../context/AuthContext';
import { useReminder, useUpdateReminderStatus, useDeleteReminder } from '../../../hooks/useApi';
import { 
    IconArrowLeft, IconEdit, IconTrash, IconCalendar, IconClock, 
    IconRepeat, IconUsers, IconTag, IconCheckCircle, IconPause, IconPlay
} from '../../components/icons';

/**
 * Format recurrence type to human-readable string
 */
const formatRecurrence = (recurrence, t) => {
    if (!recurrence) return '';
    const { type, interval = 1 } = recurrence;
    
    switch (type) {
        case 'once':
            return t('recurrence.once', { ns: 'reminders' });
        case 'daily':
            return interval === 1 
                ? t('recurrence.daily', { ns: 'reminders' })
                : t('recurrence.everyNDays', { ns: 'reminders', count: interval });
        case 'weekly':
            return interval === 1
                ? t('recurrence.weekly', { ns: 'reminders' })
                : t('recurrence.everyNWeeks', { ns: 'reminders', count: interval });
        case 'biweekly':
            return t('recurrence.biweekly', { ns: 'reminders' });
        case 'monthly':
            return interval === 1
                ? t('recurrence.monthly', { ns: 'reminders' })
                : t('recurrence.everyNMonths', { ns: 'reminders', count: interval });
        case 'quarterly':
            return t('recurrence.quarterly', { ns: 'reminders' });
        case 'yearly':
            return t('recurrence.yearly', { ns: 'reminders' });
        default:
            return t('recurrence.custom', { ns: 'reminders' });
    }
};

/**
 * Format date to local string
 */
const formatDate = (dateStr, options = {}) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString(undefined, { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        ...options
    });
};

const ReminderView = () => {
    const { t } = useI18n(['common', 'reminders']);
    const { user } = useAuth();
    const { reminderId } = useParams();
    const navigate = useNavigate();

    // React Query hooks
    const { data: reminderResult, isLoading: loading, error: queryError, refetch } = useReminder(reminderId);
    const updateStatusMutation = useUpdateReminderStatus();
    const deleteMutation = useDeleteReminder();

    const reminder = reminderResult?.data;
    const error = queryError?.message;
    const updating = updateStatusMutation.isPending;

    const handleStatusChange = async (newStatus) => {
        try {
            await updateStatusMutation.mutateAsync({ reminderId, status: newStatus });
        } catch (err) {
            alert(err.message || t('error', { ns: 'common' }));
        }
    };

    const handleDelete = async () => {
        if (!confirm(t('deleteConfirm', { ns: 'reminders' }))) return;
        
        try {
            await deleteMutation.mutateAsync(reminderId);
            navigate('/reminders');
        } catch (err) {
            alert(err.message || t('error', { ns: 'common' }));
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-4xl mx-auto px-4 py-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                    {error}
                </div>
            </div>
        );
    }

    if (!reminder) return null;

    const canEdit = reminder.myPermission === 'owner' || reminder.myPermission === 'write';
    const canDelete = reminder.myPermission === 'owner';

    return (
        <div className="max-w-4xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-4">
                    <Link
                        to="/reminders"
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                    >
                        <IconArrowLeft className="w-5 h-5" />
                    </Link>
                    <div className="flex items-center gap-3">
                        <span className="text-3xl">{reminder.icon}</span>
                        <div>
                            <h1 className="text-2xl font-bold text-gray-900">{reminder.title}</h1>
                            {reminder.category && (
                                <span className="text-sm text-gray-500">{reminder.category}</span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {canEdit && (
                        <Link
                            to={`/reminders/edit/${reminderId}`}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                            <IconEdit className="w-4 h-4 mr-1" />
                            {t('edit', { ns: 'common' })}
                        </Link>
                    )}
                    {canDelete && (
                        <button
                            onClick={handleDelete}
                            className="inline-flex items-center px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 bg-white hover:bg-red-50"
                        >
                            <IconTrash className="w-4 h-4 mr-1" />
                            {t('delete', { ns: 'common' })}
                        </button>
                    )}
                </div>
            </div>

            {/* Ownership Badge */}
            {!reminder.isOwner && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6 flex items-center gap-3">
                    <IconUsers className="w-5 h-5 text-purple-600" />
                    <div>
                        <p className="font-medium text-purple-900">
                            {t('sharedWithYou', { ns: 'reminders' })}
                        </p>
                        <p className="text-sm text-purple-700">
                            {t('ownerIs', { ns: 'reminders', owner: reminder.ownerName })}
                            {' • '}
                            {t(`permission.${reminder.myPermission}Desc`, { ns: 'reminders' })}
                        </p>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Main Content */}
                <div className="lg:col-span-2 space-y-6">
                    {/* Status & Quick Actions */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-semibold text-gray-900">
                                {t('status', { ns: 'common' })}
                            </h2>
                            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                                reminder.status === 'active' ? 'bg-green-100 text-green-800' :
                                reminder.status === 'paused' ? 'bg-yellow-100 text-yellow-800' :
                                reminder.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-600'
                            }`}>
                                {t(`status.${reminder.status}`, { ns: 'reminders' })}
                            </span>
                        </div>

                        {canEdit && reminder.status !== 'archived' && (
                            <div className="flex gap-2">
                                {reminder.status === 'active' && (
                                    <button
                                        onClick={() => handleStatusChange('paused')}
                                        disabled={updating}
                                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        <IconPause className="w-4 h-4" />
                                        {t('pause', { ns: 'reminders' })}
                                    </button>
                                )}
                                {reminder.status === 'paused' && (
                                    <button
                                        onClick={() => handleStatusChange('active')}
                                        disabled={updating}
                                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                                    >
                                        <IconPlay className="w-4 h-4" />
                                        {t('resume', { ns: 'reminders' })}
                                    </button>
                                )}
                                {reminder.status !== 'completed' && (
                                    <button
                                        onClick={() => handleStatusChange('completed')}
                                        disabled={updating}
                                        className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 border border-green-300 rounded-md text-sm font-medium text-green-700 bg-white hover:bg-green-50 disabled:opacity-50"
                                    >
                                        <IconCheckCircle className="w-4 h-4" />
                                        {t('markComplete', { ns: 'reminders' })}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Description */}
                    {reminder.description && (
                        <div className="bg-white rounded-lg shadow-sm p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-3">
                                {t('description', { ns: 'reminders' })}
                            </h2>
                            <p className="text-gray-700 whitespace-pre-wrap">
                                {reminder.description}
                            </p>
                        </div>
                    )}

                    {/* Schedule */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">
                            <IconCalendar className="w-5 h-5 inline mr-2" />
                            {t('scheduleInfo', { ns: 'reminders' })}
                        </h2>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <p className="text-sm text-gray-500">{t('startDate', { ns: 'reminders' })}</p>
                                <p className="font-medium">{formatDate(reminder.schedule?.startDate)}</p>
                            </div>
                            {reminder.schedule?.endDate && (
                                <div>
                                    <p className="text-sm text-gray-500">{t('endDate', { ns: 'reminders' })}</p>
                                    <p className="font-medium">{formatDate(reminder.schedule.endDate)}</p>
                                </div>
                            )}
                            <div>
                                <p className="text-sm text-gray-500">{t('recurrence', { ns: 'reminders' })}</p>
                                <p className="font-medium flex items-center gap-1">
                                    <IconRepeat className="w-4 h-4 text-gray-400" />
                                    {formatRecurrence(reminder.schedule?.recurrence, t)}
                                </p>
                            </div>
                            {reminder.schedule?.nextOccurrence && (
                                <div>
                                    <p className="text-sm text-gray-500">{t('nextOccurrence', { ns: 'reminders' })}</p>
                                    <p className="font-medium text-blue-600">
                                        {formatDate(reminder.schedule.nextOccurrence)}
                                    </p>
                                </div>
                            )}
                            {reminder.schedule?.time && (
                                <div>
                                    <p className="text-sm text-gray-500">{t('reminderTime', { ns: 'reminders' })}</p>
                                    <p className="font-medium flex items-center gap-1">
                                        <IconClock className="w-4 h-4 text-gray-400" />
                                        {reminder.schedule.time}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Collaborators */}
                    {reminder.collaborators && reminder.collaborators.length > 0 && (
                        <div className="bg-white rounded-lg shadow-sm p-6">
                            <h2 className="text-lg font-semibold text-gray-900 mb-4">
                                <IconUsers className="w-5 h-5 inline mr-2" />
                                {t('collaborators', { ns: 'reminders' })} ({reminder.collaborators.length})
                            </h2>
                            <div className="divide-y divide-gray-100">
                                {reminder.collaborators.map(collab => (
                                    <div key={collab.userId} className="flex items-center justify-between py-3">
                                        <div>
                                            <p className="font-medium text-gray-900">{collab.userName}</p>
                                            <p className="text-sm text-gray-500">{collab.userEmail}</p>
                                        </div>
                                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                                            collab.permission === 'write' 
                                                ? 'bg-green-100 text-green-700' 
                                                : 'bg-gray-100 text-gray-700'
                                        }`}>
                                            {t(`permission.${collab.permission}`, { ns: 'reminders' })}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Sidebar */}
                <div className="space-y-6">
                    {/* Priority */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <h3 className="text-sm font-medium text-gray-500 mb-2">
                            {t('priorityLabel', { ns: 'reminders' })}
                        </h3>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                            reminder.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                            reminder.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                            reminder.priority === 'medium' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                        }`}>
                            {t(`priority.${reminder.priority}`, { ns: 'reminders' })}
                        </span>
                    </div>

                    {/* Tags */}
                    {reminder.tags && reminder.tags.length > 0 && (
                        <div className="bg-white rounded-lg shadow-sm p-6">
                            <h3 className="text-sm font-medium text-gray-500 mb-3">
                                <IconTag className="w-4 h-4 inline mr-1" />
                                {t('tags', { ns: 'reminders' })}
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {reminder.tags.map(tag => (
                                    <span 
                                        key={tag}
                                        className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-sm"
                                    >
                                        #{tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Metadata */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <h3 className="text-sm font-medium text-gray-500 mb-3">
                            {t('details', { ns: 'common' })}
                        </h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-gray-500">{t('createdAt', { ns: 'common' })}</span>
                                <span className="text-gray-900">{formatDate(reminder.createdAt, { hour: undefined, minute: undefined })}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-gray-500">{t('updatedAt', { ns: 'common' })}</span>
                                <span className="text-gray-900">{formatDate(reminder.updatedAt, { hour: undefined, minute: undefined })}</span>
                            </div>
                            {reminder.completedAt && (
                                <div className="flex justify-between">
                                    <span className="text-gray-500">{t('completedAt', { ns: 'reminders' })}</span>
                                    <span className="text-gray-900">{formatDate(reminder.completedAt, { hour: undefined, minute: undefined })}</span>
                                </div>
                            )}
                            <div className="flex justify-between">
                                <span className="text-gray-500">{t('owner', { ns: 'reminders' })}</span>
                                <span className="text-gray-900">{reminder.ownerName}</span>
                            </div>
                        </div>
                    </div>

                    {/* Notifications */}
                    <div className="bg-white rounded-lg shadow-sm p-6">
                        <h3 className="text-sm font-medium text-gray-500 mb-3">
                            {t('notifications', { ns: 'reminders' })}
                        </h3>
                        <div className="space-y-2 text-sm">
                            <div className="flex items-center gap-2">
                                <span className={`w-2 h-2 rounded-full ${
                                    reminder.notifications?.enabled ? 'bg-green-500' : 'bg-gray-400'
                                }`}></span>
                                <span className="text-gray-700">
                                    {reminder.notifications?.enabled 
                                        ? t('notificationsEnabled', { ns: 'reminders' })
                                        : t('notificationsDisabled', { ns: 'reminders' })
                                    }
                                </span>
                            </div>
                            {reminder.notifications?.enabled && (
                                <div className="flex flex-wrap gap-1 mt-2">
                                    {reminder.notifications.channels?.map(channel => (
                                        <span key={channel} className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                                            {t(`channel.${channel}`, { ns: 'reminders' })}
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ReminderView;
